import { useState } from "react";

const SECTIONS = [
  {
    id: "s1",
    label: "Section 1",
    title: "MyFile class — basics",
    color: "#1A56DB",
    bg: "#EBF5FF",
    border: "#93C5FD",
    range: "Q1–Q4",
    questions: [
      {
        id: "Q1",
        text: `Create a class MyFile that takes a filename in __init__ and stores it as self.filename. Implement __repr__ so that printing the object shows MyFile('todo.txt').`,
        hint: `Define __repr__(self) and return an f-string like f"MyFile('{self.filename}')".`,
        tags: ["OOP basics", "__repr__"],
        code: `class MyFile:\n    def __init__(self, filename):\n        self.filename = filename\n\n    def __repr__(self):\n        return f"MyFile('{self.filename}')"`,
      },
      {
        id: "Q2",
        text: `Add a write(text) method to MyFile that saves text to the file. Instantiate MyFile("test.txt"), call write("Hello Python!"), then verify the file content.`,
        hint: `Open the file with mode "w" inside a with block. Use f.write(text).`,
        tags: ["write mode", "with block"],
        code: `def write(self, text):\n    with open(self.filename, "w") as f:\n        f.write(text)`,
      },
      {
        id: "Q3",
        text: `Add read() and append(text) methods. Write "Day 1", append "Day 2", then read and print — the output should show both lines.`,
        hint: `Use "r" to read and "a" to append. Remember to add \\n between lines when appending.`,
        tags: ["read mode", "append mode"],
        code: `def read(self):\n    with open(self.filename, "r") as f:\n        return f.read()\n\ndef append(self, text):\n    with open(self.filename, "a") as f:\n        f.write(text + "\\n")`,
      },
      {
        id: "Q4",
        text: `Add an exists() method. Update read() so that if the file doesn't exist, it prints a friendly message and returns an empty string instead of crashing.`,
        hint: `Use os.path.exists(self.filename) inside exists(). In read(), call self.exists() first before opening.`,
        tags: ["os.path", "guard clause"],
        code: `import os\n\ndef exists(self):\n    return os.path.exists(self.filename)\n\ndef read(self):\n    if not self.exists():\n        print(f"File '{self.filename}' not found.")\n        return ""\n    with open(self.filename, "r") as f:\n        return f.read()`,
      },
    ],
  },
  {
    id: "s2",
    label: "Section 2",
    title: "MyFile class — line operations",
    color: "#057A55",
    bg: "#F3FAF7",
    border: "#6EE7B7",
    range: "Q5–Q7",
    questions: [
      {
        id: "Q5",
        text: `Add read_lines() that returns a list of lines from the file. Use it inside count_lines() to return the total line count — the file must be opened only once.`,
        hint: `read_lines() uses f.readlines(). In count_lines() call len(self.read_lines()) — no second open() needed.`,
        tags: ["readlines", "DRY principle"],
        code: `def read_lines(self):\n    with open(self.filename, "r") as f:\n        return f.readlines()\n\ndef count_lines(self):\n    return len(self.read_lines())`,
      },
      {
        id: "Q6",
        text: `Explain why sorting ["CK01", "CK011", "CK02"] places "CK011" before "CK02". Then write code to sort the same list numerically by the trailing number.`,
        hint: `String sorting is lexicographic — it compares character by character. Use key=lambda x: int(x[2:]) to sort by the numeric part.`,
        tags: ["lexicographic sort", "key function"],
        code: `items = ["CK01", "CK011", "CK02"]\n\n# String sort — lexicographic\nprint(sorted(items))  # ['CK01', 'CK011', 'CK02']\n\n# Numeric sort\nprint(sorted(items, key=lambda x: int(x[2:])))  # ['CK01', 'CK02', 'CK011']`,
      },
      {
        id: "Q7",
        text: `Sort a list of log dictionaries by severity level in descending order: CRITICAL → HIGH → MEDIUM → LOW → INFO. The list is unordered and uses string severity labels.`,
        hint: `Define a priority dict {"CRITICAL": 5, "HIGH": 4, ...} and use key=lambda x: priority[x["severity"]], reverse=True.`,
        tags: ["custom sort", "reverse=True"],
        code: `priority = {"CRITICAL": 5, "HIGH": 4, "MEDIUM": 3, "LOW": 2, "INFO": 1}\nlogs = [{"severity": "INFO"}, {"severity": "CRITICAL"}, {"severity": "HIGH"}]\n\nsorted_logs = sorted(logs, key=lambda x: priority[x["severity"]], reverse=True)`,
      },
    ],
  },
  {
    id: "s3",
    label: "Section 3",
    title: "Log writing & exception handling",
    color: "#92400E",
    bg: "#FFFBEB",
    border: "#FCD34D",
    range: "Q8–Q12",
    questions: [
      {
        id: "Q8",
        text: `Write a method write_log(severity, message) that appends a log entry in the format:\n2024-10-01 09:00 | CRITICAL | Node worker-01 NotReady\nSeverity must be one of: CRITICAL / HIGH / MEDIUM / LOW / INFO.`,
        hint: `Use datetime.now().strftime("%Y-%m-%d %H:%M") for the timestamp. Format the line as f"{ts} | {severity} | {message}\\n".`,
        tags: ["datetime", "strftime", "append"],
        code: `from datetime import datetime\n\ndef write_log(self, severity, message):\n    ts = datetime.now().strftime("%Y-%m-%d %H:%M")\n    entry = f"{ts} | {severity} | {message}\\n"\n    with open(self.filename, "a") as f:\n        f.write(entry)`,
      },
      {
        id: "Q9",
        text: `Simulate a scenario where you open a file using a with block, and an exception occurs in the middle of writing. Handle the exception gracefully and ensure a fallback message is written.`,
        hint: `Wrap the with open(...) block in a try/except. Catch IOError or Exception. The with block still closes the file even if an exception occurs inside it.`,
        tags: ["try/except", "with block", "IOError"],
        code: `try:\n    with open("log.txt", "a") as f:\n        f.write("Starting write...\\n")\n        raise ValueError("Simulated error mid-write")\n        f.write("This never runs\\n")\nexcept Exception as e:\n    with open("log.txt", "a") as f:\n        f.write(f"FALLBACK: Write failed — {e}\\n")`,
      },
      {
        id: "Q10",
        text: `Refactor write_log so that severity-specific methods exist: log_critical(msg), log_info(msg), etc., each internally calling a shared _write_entry(severity, msg) method.`,
        hint: `The public methods like log_critical(msg) simply call self._write_entry("CRITICAL", msg). The private _write_entry does all the file writing. Single responsibility principle.`,
        tags: ["refactoring", "private methods", "SRP"],
        code: `def _write_entry(self, severity, message):\n    ts = datetime.now().strftime("%Y-%m-%d %H:%M")\n    with open(self.filename, "a") as f:\n        f.write(f"{ts} | {severity} | {message}\\n")\n\ndef log_critical(self, msg): self._write_entry("CRITICAL", msg)\ndef log_info(self, msg):     self._write_entry("INFO", msg)\ndef log_high(self, msg):     self._write_entry("HIGH", msg)`,
      },
      {
        id: "Q11",
        text: `You have a log file with multiple paragraphs separated by blank lines. Write code to count the number of lines between the first and second blank line (i.e. inside the second paragraph).`,
        hint: `Read all lines with readlines(). Track blank lines with a counter. Collect lines between the 1st and 2nd blank line using index slicing or a state flag.`,
        tags: ["readlines", "paragraph parsing"],
        code: `with open("log.txt", "r") as f:\n    lines = f.readlines()\n\nblanks = [i for i, l in enumerate(lines) if l.strip() == ""]\nif len(blanks) >= 2:\n    para = lines[blanks[0]+1 : blanks[1]]\n    print(f"Lines in 2nd paragraph: {len(para)}")`,
      },
      {
        id: "Q12",
        text: `Given three text files each containing a paragraph, write a function that merges all three into a single output file, separating each with a blank line. Minimize the number of with blocks used.`,
        hint: `Open multiple files in one with statement: with open(f1) as a, open(f2) as b, open(f3) as c, open(out, "w") as o:. Write each with "\\n" in between.`,
        tags: ["multi-file with", "merge"],
        code: `def merge_files(f1, f2, f3, output):\n    with open(f1) as a, open(f2) as b, open(f3) as c, open(output, "w") as o:\n        o.write(a.read())\n        o.write("\\n\\n")\n        o.write(b.read())\n        o.write("\\n\\n")\n        o.write(c.read())`,
      },
    ],
  },
  {
    id: "s4",
    label: "Section 4",
    title: "HandleFile class — full implementation",
    color: "#5521B5",
    bg: "#F5F3FF",
    border: "#C4B5FD",
    range: "Q13–Q20",
    questions: [
      {
        id: "Q13",
        text: `Create a class HandleFile. Add a write_logs(entries) method that accepts a list of (severity, message) tuples and writes them all in one file open — not one with block per entry.`,
        hint: `Open once in "w" or "a" mode, loop through entries and call f.write() inside the loop. Close only once when the with block exits.`,
        tags: ["batch write", "single with"],
        code: `class HandleFile:\n    def __init__(self, filename):\n        self.filename = filename\n\n    def write_logs(self, entries):\n        with open(self.filename, "a") as f:\n            for severity, message in entries:\n                ts = datetime.now().strftime("%Y-%m-%d %H:%M")\n                f.write(f"{ts} | {severity} | {message}\\n")`,
      },
      {
        id: "Q14",
        text: `Add a flexible read(line_by_line=False) method to HandleFile. When False it returns the full file as a string; when True it yields lines one at a time using a generator.`,
        hint: `Use f.read() for full content. For line-by-line, use yield inside a for line in f: loop — this makes it a generator that doesn't load the whole file at once.`,
        tags: ["generator", "yield", "read modes"],
        code: `def read(self, line_by_line=False):\n    with open(self.filename, "r") as f:\n        if line_by_line:\n            for line in f:\n                yield line.rstrip()\n        else:\n            return f.read()`,
      },
      {
        id: "Q15",
        text: `Add an append_log(severity, message) method to HandleFile. It should append a new alert to the existing log file without overwriting previous entries.`,
        hint: `Open with "a" mode. Format the line identically to your log format. Appending never truncates the file.`,
        tags: ["append mode", "log rotation"],
        code: `def append_log(self, severity, message):\n    ts = datetime.now().strftime("%Y-%m-%d %H:%M")\n    with open(self.filename, "a") as f:\n        f.write(f"{ts} | {severity} | {message}\\n")`,
      },
      {
        id: "Q16",
        text: `Add a search(severity) method to HandleFile that reads the file and prints only lines that contain the given severity level (e.g. only CRITICAL lines).`,
        hint: `Read the file line by line. Use if severity in line to filter. Print matching lines with .strip() to remove trailing newlines.`,
        tags: ["filter", "string search"],
        code: `def search(self, severity):\n    for line in self._read_lines():\n        if severity in line:\n            print(line.strip())`,
      },
      {
        id: "Q17",
        text: `Add a stats() method to HandleFile that returns a dict with the count of each severity type, e.g. {"CRITICAL": 3, "INFO": 7}. Open the file only once.`,
        hint: `Use collections.Counter or a plain dict. For each line, split on "|" and check index 1 (the severity part) after stripping whitespace.`,
        tags: ["Counter", "statistics", "split"],
        code: `from collections import Counter\n\ndef stats(self):\n    counts = Counter()\n    for line in self._read_lines():\n        parts = line.split("|")\n        if len(parts) >= 2:\n            counts[parts[1].strip()] += 1\n    return dict(counts)`,
      },
      {
        id: "Q18",
        text: `Refactor HandleFile to reduce the number of times the file is opened. Identify which methods each open the file separately and consolidate them into a shared _read_lines() helper.`,
        hint: `Create a private _read_lines() that opens and returns f.readlines(). Then search(), stats(), and count_lines() all call this instead of opening the file themselves.`,
        tags: ["refactoring", "helper method", "DRY"],
        code: `def _read_lines(self):\n    with open(self.filename, "r") as f:\n        return f.readlines()\n\n# Now reuse everywhere:\ndef count_lines(self):\n    return len(self._read_lines())\n\ndef search(self, severity):\n    for line in self._read_lines():\n        if severity in line:\n            print(line.strip())`,
      },
      {
        id: "Q19",
        text: `Update stats() so it also prints a formatted summary table to the console:\nCRITICAL : 3 ████████\nINFO     : 7 ████████████████████`,
        hint: `After computing counts, find the max count. Scale each bar with "█" * int((count/max_count) * 20). Use f"{key:<10}" for column alignment.`,
        tags: ["f-string formatting", "bar chart"],
        code: `def stats(self):\n    counts = self._count_by_severity()\n    max_val = max(counts.values(), default=1)\n    for key, val in counts.items():\n        bar = "█" * int((val / max_val) * 20)\n        print(f"{key:<10}: {val} {bar}")`,
      },
      {
        id: "Q20",
        text: `What happens if the with block in a file write method raises an exception halfway through? Does the file get closed? Is the partially-written content saved? Write a test to confirm.`,
        hint: `Yes — the with block calls __exit__ and closes the file even on exception. Content written before the exception is flushed and saved. Write a function that raises mid-write and inspect the file after.`,
        tags: ["context manager", "__exit__", "exception safety"],
        code: `def test_partial_write():\n    try:\n        with open("partial.txt", "w") as f:\n            f.write("Line 1 written\\n")\n            raise RuntimeError("Crash mid-write")\n            f.write("Line 2 never written\\n")\n    except RuntimeError:\n        pass\n    # File is closed; Line 1 IS saved\n    with open("partial.txt") as f:\n        print(f.read())  # → "Line 1 written"`,
      },
    ],
  },
  {
    id: "s5",
    label: "Section 5",
    title: "CSV file handling",
    color: "#9B1C1C",
    bg: "#FEF2F2",
    border: "#FCA5A5",
    range: "Q21–Q25",
    questions: [
      {
        id: "Q21",
        text: `Manually create nodes.csv with header node,status,role and three rows: master-01/Ready/control-plane, worker-01/Ready/worker, worker-02/NotReady/worker. Use csv.writer — no pandas.`,
        hint: `Use csv.writer(f). Call writer.writerow(["node","status","role"]) first, then writer.writerows(rows). Open with newline="" to avoid blank lines on Windows.`,
        tags: ["csv.writer", "writerows"],
        code: `import csv\n\nrows = [\n    ["master-01", "Ready",    "control-plane"],\n    ["worker-01", "Ready",    "worker"],\n    ["worker-02", "NotReady", "worker"],\n]\nwith open("nodes.csv", "w", newline="") as f:\n    writer = csv.writer(f)\n    writer.writerow(["node", "status", "role"])\n    writer.writerows(rows)`,
      },
      {
        id: "Q22",
        text: `Read nodes.csv using csv.reader (skip the header, print each row). Then re-read using csv.DictReader and print only the node and status columns.`,
        hint: `With csv.reader, call next(reader) to skip the header. With csv.DictReader, the header is auto-consumed — access columns as row["node"].`,
        tags: ["csv.reader", "DictReader", "skip header"],
        code: `# csv.reader\nwith open("nodes.csv") as f:\n    reader = csv.reader(f)\n    next(reader)  # skip header\n    for row in reader:\n        print(row)\n\n# csv.DictReader\nwith open("nodes.csv") as f:\n    for row in csv.DictReader(f):\n        print(row["node"], row["status"])`,
      },
      {
        id: "Q23",
        text: `Using csv.DictReader, filter and print only the rows where status == "NotReady". No pandas allowed.`,
        hint: `Iterate over DictReader rows and use if row["status"] == "NotReady": to filter. Print the full row dict or individual fields.`,
        tags: ["filter", "DictReader"],
        code: `with open("nodes.csv") as f:\n    for row in csv.DictReader(f):\n        if row["status"] == "NotReady":\n            print(row)`,
      },
      {
        id: "Q24",
        text: `Write a function count_by_status(filepath) that reads the CSV and returns a dict of how many nodes have each status, e.g. {"Ready": 2, "NotReady": 1}.`,
        hint: `Use csv.DictReader and a collections.Counter or a plain dict with .get(status, 0) + 1. Return the dict at the end.`,
        tags: ["Counter", "aggregation"],
        code: `def count_by_status(filepath):\n    from collections import Counter\n    counts = Counter()\n    with open(filepath) as f:\n        for row in csv.DictReader(f):\n            counts[row["status"]] += 1\n    return dict(counts)`,
      },
      {
        id: "Q25",
        text: `Write a function that updates a specific node's status in the CSV. E.g. change worker-02's status from NotReady to Ready. Read all rows, modify in memory, and write back — no pandas.`,
        hint: `Read all rows into a list with DictReader. Modify the matching row's "status" key. Write back with DictWriter using the same fieldnames and writer.writeheader().`,
        tags: ["DictWriter", "in-memory edit", "writeheader"],
        code: `def update_status(filepath, node_name, new_status):\n    with open(filepath) as f:\n        rows = list(csv.DictReader(f))\n        fieldnames = rows[0].keys() if rows else []\n    for row in rows:\n        if row["node"] == node_name:\n            row["status"] = new_status\n    with open(filepath, "w", newline="") as f:\n        writer = csv.DictWriter(f, fieldnames=fieldnames)\n        writer.writeheader()\n        writer.writerows(rows)`,
      },
    ],
  },
];

const allQuestions = SECTIONS.flatMap((s) => s.questions);
const TOTAL = allQuestions.length;

function CodeBlock({ code }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };
  return (
    <div style={{ position: "relative", marginTop: 12 }}>
      <pre style={{
        background: "#0F172A",
        color: "#E2E8F0",
        borderRadius: 10,
        padding: "14px 16px",
        fontSize: 12.5,
        lineHeight: 1.7,
        overflowX: "auto",
        margin: 0,
        fontFamily: "'JetBrains Mono', 'Fira Code', 'Courier New', monospace",
        border: "1px solid #1E293B",
      }}>
        <code>{code}</code>
      </pre>
      <button onClick={copy} style={{
        position: "absolute", top: 8, right: 8,
        background: copied ? "#22C55E22" : "#1E293B",
        border: `1px solid ${copied ? "#22C55E" : "#334155"}`,
        borderRadius: 6, padding: "4px 10px",
        color: copied ? "#22C55E" : "#94A3B8",
        fontSize: 11, cursor: "pointer", transition: "all 0.2s",
        fontFamily: "inherit",
      }}>
        {copied ? "✓ copied" : "copy"}
      </button>
    </div>
  );
}

function QuestionCard({ q, section, idx, globalIdx }) {
  const [open, setOpen] = useState(false);
  const [showCode, setShowCode] = useState(false);

  return (
    <div
      onClick={() => setOpen(!open)}
      style={{
        background: "#fff",
        border: `1.5px solid ${open ? section.border : "#E2E8F0"}`,
        borderRadius: 14,
        padding: "16px 18px",
        cursor: "pointer",
        transition: "border-color 0.2s, box-shadow 0.2s",
        boxShadow: open ? `0 0 0 3px ${section.bg}` : "none",
      }}
    >
      <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
        <span style={{
          background: section.bg, color: section.color,
          fontWeight: 600, fontSize: 11.5, padding: "3px 10px",
          borderRadius: 99, flexShrink: 0, marginTop: 2,
          border: `1px solid ${section.border}`,
          letterSpacing: "0.02em",
        }}>{q.id}</span>
        <p style={{
          flex: 1, margin: 0, fontSize: 14, color: "#1E293B",
          lineHeight: 1.65, whiteSpace: "pre-line",
        }}>{q.text}</p>
        <span style={{
          color: "#94A3B8", fontSize: 18, flexShrink: 0,
          transform: open ? "rotate(180deg)" : "rotate(0deg)",
          transition: "transform 0.22s", marginTop: 2,
          userSelect: "none",
        }}>⌄</span>
      </div>

      {open && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid #F1F5F9" }}
        >
          {/* Hint */}
          <div style={{
            background: section.bg, borderRadius: 10, padding: "12px 14px",
            border: `1px solid ${section.border}`,
          }}>
            <p style={{ margin: "0 0 4px 0", fontSize: 11.5, fontWeight: 600, color: section.color, textTransform: "uppercase", letterSpacing: "0.06em" }}>Hint</p>
            <p style={{ margin: 0, fontSize: 13.5, color: "#374151", lineHeight: 1.6 }}>{q.hint}</p>
          </div>

          {/* Tags */}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 10 }}>
            {q.tags.map((t) => (
              <span key={t} style={{
                fontSize: 11, padding: "3px 9px", borderRadius: 99,
                background: "#F8FAFC", border: "1px solid #E2E8F0",
                color: "#64748B", fontWeight: 500,
              }}>{t}</span>
            ))}
          </div>

          {/* Code toggle */}
          <button
            onClick={() => setShowCode(!showCode)}
            style={{
              marginTop: 12, background: "transparent",
              border: `1px solid ${section.border}`, borderRadius: 8,
              padding: "6px 14px", fontSize: 12.5, cursor: "pointer",
              color: section.color, fontWeight: 500,
              display: "flex", alignItems: "center", gap: 6,
            }}
          >
            <span>{showCode ? "▲" : "▼"}</span>
            {showCode ? "hide sample answer" : "show sample answer"}
          </button>

          {showCode && <CodeBlock code={q.code} />}
        </div>
      )}
    </div>
  );
}

export default function PythonFileHandlingTest() {
  const [activeSection, setActiveSection] = useState("all");
  const [search, setSearch] = useState("");
  const [solved, setSolved] = useState({});

  const solvedCount = Object.values(solved).filter(Boolean).length;

  const filtered = SECTIONS.map((s) => ({
    ...s,
    questions: s.questions.filter((q) =>
      (activeSection === "all" || s.id === activeSection) &&
      (search === "" ||
        q.text.toLowerCase().includes(search.toLowerCase()) ||
        q.tags.some((t) => t.toLowerCase().includes(search.toLowerCase())))
    ),
  })).filter((s) => s.questions.length > 0);

  const pct = Math.round((solvedCount / TOTAL) * 100);

  return (
    <div style={{
      minHeight: "100vh",
      background: "linear-gradient(160deg, #F8FAFC 0%, #EFF6FF 50%, #F5F3FF 100%)",
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    }}>
      {/* Hero header */}
      <div style={{
        background: "#0F172A",
        color: "#fff",
        padding: "48px 32px 36px",
        textAlign: "center",
        position: "relative",
        overflow: "hidden",
      }}>
        {/* Background accent */}
        <div style={{
          position: "absolute", top: -60, left: "50%", transform: "translateX(-50%)",
          width: 600, height: 200,
          background: "radial-gradient(ellipse, #6366F1 0%, transparent 70%)",
          opacity: 0.2, pointerEvents: "none",
        }} />

        <p style={{ margin: "0 0 10px", fontSize: 12, letterSpacing: "0.15em", color: "#6366F1", fontWeight: 600, textTransform: "uppercase" }}>
          Python Practice
        </p>
        <h1 style={{ margin: "0 0 12px", fontSize: 38, fontWeight: 700, letterSpacing: "-0.02em", lineHeight: 1.15 }}>
          File Handling
          <span style={{ color: "#818CF8" }}> Questions</span>
        </h1>
        <p style={{ margin: "0 0 28px", color: "#94A3B8", fontSize: 15, maxWidth: 480, marginLeft: "auto", marginRight: "auto", lineHeight: 1.6 }}>
          25 questions across MyFile class, log writing, HandleFile, exception handling, and CSV operations.
        </p>

        {/* Stats row */}
        <div style={{ display: "flex", justifyContent: "center", gap: 40, marginBottom: 28 }}>
          {[
            { val: TOTAL, label: "questions" },
            { val: 5, label: "sections" },
            { val: solvedCount, label: "marked done" },
          ].map(({ val, label }) => (
            <div key={label} style={{ textAlign: "center" }}>
              <div style={{ fontSize: 28, fontWeight: 700, color: "#E2E8F0" }}>{val}</div>
              <div style={{ fontSize: 12, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.08em" }}>{label}</div>
            </div>
          ))}
        </div>

        {/* Progress bar */}
        <div style={{ maxWidth: 420, margin: "0 auto" }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
            <span style={{ fontSize: 12, color: "#64748B" }}>progress</span>
            <span style={{ fontSize: 12, color: "#818CF8", fontWeight: 600 }}>{pct}%</span>
          </div>
          <div style={{ height: 6, background: "#1E293B", borderRadius: 99, overflow: "hidden" }}>
            <div style={{
              height: "100%", borderRadius: 99,
              background: "linear-gradient(90deg, #6366F1, #818CF8)",
              width: `${pct}%`, transition: "width 0.4s ease",
            }} />
          </div>
        </div>
      </div>

      {/* Controls */}
      <div style={{
        position: "sticky", top: 0, zIndex: 10,
        background: "rgba(248,250,252,0.92)", backdropFilter: "blur(8px)",
        borderBottom: "1px solid #E2E8F0",
        padding: "14px 32px",
        display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap",
      }}>
        {/* Search */}
        <div style={{ position: "relative", flex: 1, minWidth: 220 }}>
          <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#94A3B8", fontSize: 15 }}>🔍</span>
          <input
            placeholder="Search questions or tags…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: "100%", padding: "9px 12px 9px 36px",
              border: "1.5px solid #E2E8F0", borderRadius: 10, fontSize: 13.5,
              background: "#fff", color: "#1E293B", outline: "none",
              boxSizing: "border-box",
            }}
          />
        </div>

        {/* Section filter */}
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <button onClick={() => setActiveSection("all")} style={{
            padding: "7px 14px", borderRadius: 8, fontSize: 12.5, cursor: "pointer",
            fontWeight: 500, border: `1.5px solid ${activeSection === "all" ? "#6366F1" : "#E2E8F0"}`,
            background: activeSection === "all" ? "#EEF2FF" : "#fff",
            color: activeSection === "all" ? "#4338CA" : "#64748B",
          }}>All</button>
          {SECTIONS.map((s) => (
            <button key={s.id} onClick={() => setActiveSection(activeSection === s.id ? "all" : s.id)} style={{
              padding: "7px 14px", borderRadius: 8, fontSize: 12.5, cursor: "pointer",
              fontWeight: 500, border: `1.5px solid ${activeSection === s.id ? s.color : "#E2E8F0"}`,
              background: activeSection === s.id ? s.bg : "#fff",
              color: activeSection === s.id ? s.color : "#64748B",
            }}>{s.label}</button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth: 820, margin: "0 auto", padding: "32px 20px 60px" }}>
        {filtered.map((section) => (
          <div key={section.id} style={{ marginBottom: 40 }}>
            {/* Section header */}
            <div style={{
              display: "flex", alignItems: "center", gap: 12,
              marginBottom: 16, paddingBottom: 12,
              borderBottom: `2px solid ${section.border}`,
            }}>
              <span style={{
                background: section.bg, color: section.color,
                fontSize: 11, fontWeight: 700, padding: "4px 12px",
                borderRadius: 99, border: `1px solid ${section.border}`,
                textTransform: "uppercase", letterSpacing: "0.08em",
              }}>{section.label}</span>
              <h2 style={{ margin: 0, fontSize: 17, fontWeight: 600, color: "#0F172A" }}>{section.title}</h2>
              <span style={{ marginLeft: "auto", fontSize: 12, color: "#94A3B8", fontWeight: 500 }}>{section.range}</span>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {section.questions.map((q, i) => {
                const globalIdx = allQuestions.findIndex((aq) => aq.id === q.id);
                return (
                  <div key={q.id} style={{ position: "relative" }}>
                    {/* Mark done checkbox */}
                    <div
                      onClick={() => setSolved((p) => ({ ...p, [q.id]: !p[q.id] }))}
                      title={solved[q.id] ? "Mark undone" : "Mark done"}
                      style={{
                        position: "absolute", top: 18, right: -36,
                        width: 22, height: 22, borderRadius: 6,
                        border: `2px solid ${solved[q.id] ? section.color : "#CBD5E1"}`,
                        background: solved[q.id] ? section.bg : "#fff",
                        cursor: "pointer", display: "flex", alignItems: "center",
                        justifyContent: "center", zIndex: 2,
                        transition: "all 0.15s",
                      }}
                    >
                      {solved[q.id] && <span style={{ fontSize: 12, color: section.color, fontWeight: 700 }}>✓</span>}
                    </div>
                    <QuestionCard q={q} section={section} idx={i} globalIdx={globalIdx} />
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {filtered.length === 0 && (
          <div style={{ textAlign: "center", padding: "60px 20px", color: "#94A3B8" }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>🔍</div>
            <p style={{ fontSize: 15 }}>No questions match your search.</p>
          </div>
        )}
      </div>
    </div>
  );
}
